import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { SpendUnderManagement as SpendUnderManagementType } from "../../../types/contractDashboard";
import styles from "./spendUnderManagement.module.scss";

interface SpendUnderManagementProps {
  data: SpendUnderManagementType;
}

const SpendUnderManagement: React.FC<SpendUnderManagementProps> = ({
  data,
}) => {
  // Determine which data format to use (prioritize new API format)
  const sourceData = useMemo(() => {
    const formallySourced =
      data.formallySourcedPct ?? data.underContractPct ?? 0;
    const notSourced = data.notSourcedPct ?? data.notCoveredPct ?? 0;
    const primaryLabel =
      data.formallySourcedPct !== undefined
        ? "Formally Sourced"
        : "Under Contract";
    const secondaryLabel =
      data.notSourcedPct !== undefined ? "Not Sourced" : "Not Covered";

    return {
      primaryValue: formallySourced,
      secondaryValue: notSourced,
      primaryLabel,
      secondaryLabel,
    };
  }, [
    data.formallySourcedPct,
    data.notSourcedPct,
    data.underContractPct,
    data.notCoveredPct,
  ]);

  // Memoize chart data to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    return [
      {
        name: sourceData.primaryLabel,
        value: sourceData.primaryValue,
        color: "#08ADC2",
      },
      {
        name: sourceData.secondaryLabel,
        value: sourceData.secondaryValue,
        color: "#9ca3af",
      },
    ];
  }, [sourceData]);

  // Custom label component rendered in the center of donut
  const renderCenterLabel = () => {
    return (
      <g>
        <text
          x="50%"
          y="45%"
          textAnchor="middle"
          dominantBaseline="middle"
          className={styles.centerPercentage}
        >
          {sourceData.primaryValue}%
        </text>
        <text
          x="50%"
          y="55%"
          textAnchor="middle"
          dominantBaseline="middle"
          className={styles.centerLabel}
        >
          {sourceData.primaryLabel}
        </text>
      </g>
    );
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box className={styles.tooltip}>
          <Box className={styles.tooltipHeader}>
            <Box
              className={styles.tooltipDot}
              style={{ background: data.payload.color }}
            />
            <Typography variant="body2" className={styles.tooltipName}>
              {data.name}
            </Typography>
          </Box>
          <Typography variant="body2" className={styles.tooltipValue}>
            {data.value}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Handle empty or invalid data
  const hasValidData =
    (data.formallySourcedPct ?? data.underContractPct ?? 0) > 0 ||
    (data.notSourcedPct ?? data.notCoveredPct ?? 0) > 0;

  if (!data || !hasValidData) {
    return (
      <Box className={styles.container}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Spend Under Management
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            % of total spend sourced through formal sourcing processes.
          </Typography>
        </Box>
        <Box className={styles.emptyState}>
          <Typography variant="body2" className={styles.emptyText}>
            No spend management data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Spend Under Management
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          % of total spend sourced through formal sourcing processes.
        </Typography>
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
              animationDuration={800}
              animationBegin={0}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {renderCenterLabel()}
          </PieChart>
        </ResponsiveContainer>

        <Box className={styles.legend}>
          {chartData.map((entry, index) => (
            <Box key={index} className={styles.legendItem}>
              <Box
                className={styles.legendColor}
                style={{ background: entry.color }}
              />
              <Typography variant="body2" className={styles.legendLabel}>
                {entry.name} ({entry.value}%)
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(SpendUnderManagement);
